import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useId, useState } from "react";
import { ApiError } from "../lib/api";
import {
  answerQuestion,
  type DrawnQuestion,
  type Entry,
  type EntryKind,
  type Frise,
  fetchFrise,
  fetchNextQuestion,
  fetchSettings,
  LIFE_STAGE_CHIPS,
  LIFE_STAGE_NAMES,
  LIFE_STAGE_WHEN,
  type NextQuestion,
  postponeQuestion,
  queryKeys,
  type StageGap,
  skipQuestion,
} from "./api";
import { AppPage } from "./app-nav";
import type { EntryForm } from "./entry-body";
import { EntrySheet } from "./entry-form";
import { SessionGate } from "./session-gate";

/**
 * Les questions (`/questions`, planche « Questions » ; spec, section 1 ecran 4 et section 3.6).
 * Une question a la fois, tiree par le serveur selon les trous de la frise. « Repondre » ouvre
 * l'ecran d'un point prerempli (piste, type, et « Quand ? » pour le collège, le lycée ou la fac
 * quand la date de naissance est connue) ; l'envoi passe par la route de reponse, qui marque la
 * question. Une question de concert ouvre le point « a dater » lui-meme, focus sur « Quand ? ».
 */
export function QuestionsPage() {
  const next = useQuery({
    queryKey: queryKeys.questions,
    queryFn: fetchNextQuestion,
    retry: false,
  });
  const frise = useQuery({ queryKey: queryKeys.frise, queryFn: fetchFrise, retry: false });
  if (next.isPending || frise.isPending) {
    return (
      <p className="text-muted-foreground" role="status">
        Chargement...
      </p>
    );
  }
  if (next.isError) return <SessionGate error={next.error} retry={() => void next.refetch()} />;
  if (frise.isError) return <SessionGate error={frise.error} retry={() => void frise.refetch()} />;
  return <QuestionsView frise={frise.data} next={next.data} />;
}

/** Le formulaire prerempli d'une reponse a une question de la banque. */
export function answerForm(
  question: DrawnQuestion,
  frise: Frise,
  birthDate: string | null,
): Partial<EntryForm> {
  const track = frise.tracks.find((candidate) => candidate.slug === question.track);
  const kind: EntryKind = question.expects === "periode" ? "period" : "point";
  const when =
    birthDate !== null && question.lifeStage !== null
      ? (LIFE_STAGE_WHEN[question.lifeStage] ?? "")
      : "";
  return { kind, startRaw: when, ...(track === undefined ? {} : { trackId: track.id }) };
}

function QuestionsView({ next, frise }: { next: NextQuestion; frise: Frise }) {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: queryKeys.settings, queryFn: fetchSettings, retry: false });
  const [answering, setAnswering] = useState(false);
  const id = useId();
  const { question } = next;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.questions });
  };
  const move = useMutation({
    mutationFn: ({ action, questionId }: { action: "later" | "skip"; questionId: string }) =>
      action === "later" ? postponeQuestion(questionId) : skipQuestion(questionId),
    onSuccess: refresh,
  });

  const track = question === null ? undefined : frise.tracks.find((t) => t.slug === question.track);
  const concert: Entry | null =
    question === null || question.entryId === null
      ? null
      : (frise.entries.find((entry) => entry.id === question.entryId) ?? null);

  return (
    <AppPage>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="tl-display m-0 text-[28px]">Questions</h1>
        <span className="text-[13px] text-muted-foreground">
          {next.answered} {next.answered > 1 ? "répondues" : "répondue"} · {next.total} en tout
        </span>
      </div>

      {question === null ? (
        <section className="flex max-w-xl flex-col gap-2 rounded-2xl border border-border bg-card p-5">
          <p className="tl-display m-0 text-2xl">Tu as fait le tour des questions.</p>
          <p className="m-0 text-sm text-muted-foreground">
            Il n'en reste aucune pour l'instant. Ta frise t'attend.
          </p>
        </section>
      ) : (
        <section
          aria-labelledby={`${id}-question`}
          className="flex max-w-xl flex-col gap-4 rounded-2xl border border-border bg-card px-5 py-[22px]"
        >
          <span className="inline-flex items-center gap-1.5 self-start rounded-full border border-border px-2.5 py-1 text-xs text-[#C9D0DE]">
            <span
              aria-hidden="true"
              className="size-2 rounded-full"
              style={{ background: track?.color ?? "#C9D0DE" }}
            />
            {track?.name ?? question.track}
            {question.lifeStage === null ? null : ` · ${LIFE_STAGE_CHIPS[question.lifeStage]}`}
          </span>
          <p className="m-0 text-2xl leading-[1.3] font-semibold" id={`${id}-question`}>
            {question.text}
          </p>
          <p className="m-0 text-[13px] text-muted-foreground">
            Pourquoi celle-ci : {question.reason}
          </p>
          <button
            className="min-h-12 rounded-[10px] bg-primary text-base font-semibold text-primary-foreground disabled:opacity-60"
            // Le prerempli lit la date de naissance : on attend les reglages.
            disabled={move.isPending || settings.isPending}
            onClick={() => setAnswering(true)}
            type="button"
          >
            Répondre
          </button>
          <div className="grid grid-cols-2 gap-2.5">
            <button
              className="min-h-11 rounded-[10px] border border-input text-[15px] disabled:opacity-60"
              disabled={move.isPending}
              onClick={() => move.mutate({ action: "later", questionId: question.id })}
              type="button"
            >
              Plus tard
            </button>
            <button
              className="min-h-11 rounded-[10px] border border-input text-[15px] disabled:opacity-60"
              disabled={move.isPending}
              onClick={() => move.mutate({ action: "skip", questionId: question.id })}
              type="button"
            >
              Passer
            </button>
          </div>
          {move.isError ? (
            <p className="m-0 text-sm text-destructive" role="alert">
              {move.error instanceof ApiError ? move.error.message : "Ça n'a pas abouti. Réessaie."}
            </p>
          ) : null}
        </section>
      )}

      <GapBars gaps={next.gaps} />

      {answering && question !== null ? (
        <EntrySheet
          entry={concert}
          focusWhen={concert !== null}
          heading="Répondre"
          initial={
            concert === null ? answerForm(question, frise, settings.data?.birthDate ?? null) : {}
          }
          onClose={() => setAnswering(false)}
          onSaved={refresh}
          submit={(body) => answerQuestion(question.id, body)}
          tracks={frise.tracks}
        />
      ) : null}
    </AppPage>
  );
}

/** La jauge « Ou ta frise est encore vide » : un trait par periode de vie, au prorata du maximum. */
function GapBars({ gaps }: { gaps: readonly StageGap[] }) {
  const id = useId();
  const max = Math.max(0, ...gaps.map((gap) => gap.count));
  return (
    <section aria-labelledby={`${id}-trous`} className="flex max-w-xl flex-col gap-2.5">
      <h2 className="m-0 text-[15px] font-semibold" id={`${id}-trous`}>
        Où ta frise est encore vide
      </h2>
      {gaps.length === 0 ? (
        <p className="m-0 text-sm text-muted-foreground">
          Renseigne ta date de naissance pour voir quelles périodes de ta vie sont encore vides.{" "}
          <Link className="text-tl-accent underline" to="/reglages">
            Aller aux réglages
          </Link>
        </p>
      ) : (
        <>
          <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
            {gaps.map((gap) => (
              <li
                className="grid grid-cols-[92px_minmax(0,1fr)_28px] items-center gap-2.5 text-sm"
                key={gap.stage}
              >
                <span>{LIFE_STAGE_NAMES[gap.stage]}</span>
                <span
                  aria-hidden="true"
                  className="h-2.5 overflow-hidden rounded-full bg-[rgba(243,238,227,0.10)]"
                >
                  <span
                    className="block h-2.5 rounded-full bg-[#C9D0DE]"
                    data-testid={`jauge-${gap.stage}`}
                    style={{ width: `${max === 0 ? 0 : Math.round((gap.count / max) * 100)}%` }}
                  />
                </span>
                <span className="text-right text-[#C9D0DE]">
                  {gap.count}
                  <span className="sr-only"> {gap.count > 1 ? "points" : "point"}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="m-0 mt-1 text-[13px] text-muted-foreground">
            Les questions vont d'abord là où il y a le moins de points.
          </p>
        </>
      )}
    </section>
  );
}
